import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import AdatisLogo from "../../../../Components/Common/AdatisLogo";
import HelpArticleLink from "../../../../Components/Common/HelpArticleLink";
import StudioMembershipCard from "../../../../Components/Studio/StudioMembershipCard";
import {
  clearIdentitySession,
  clearPreferredTenantId,
  getIdentitySession,
  getLastTenantId,
  getPreferredTenantId,
  isMembershipEnterable,
  setIdentitySession,
  setPreferredTenantId,
  setStudioSwitchBanner,
  StudioMembership,
} from "../../../../helpers/identity_storage";
import { HELP_ARTICLE_ACCESS_LOGIN } from "../../../../helpers/helpCenter";
import { resolvePostAuthRedirect } from "../../../../helpers/postAuthRedirect";
import {
  acceptMembership,
  declineMembership,
  getAuthSession,
  selectTenant,
} from "../../../../services/authService";
import { loginSuccess } from "../../../../slices/auth/login/reducer";
import { storeGoogleSignupDraft } from "../../../Authentication/GoogleCallback";
import ParticlesAuth from "../../../AuthenticationInner/ParticlesAuth";

const SelectStudio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const continueButtonRef = useRef<HTMLButtonElement | null>(null);
  const [busyTenantId, setBusyTenantId] = useState<number | null>(null);
  const [busyMembershipId, setBusyMembershipId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [rememberPreferred, setRememberPreferred] = useState(false);
  const [memberships, setMemberships] = useState<StudioMembership[]>(
    () => getIdentitySession()?.memberships || []
  );

  const session = getIdentitySession();
  const lastTenantId = session?.lastTenantId ?? getLastTenantId();
  const preferredTenantId = getPreferredTenantId();

  const continueMembership = useMemo(() => {
    const preferred =
      preferredTenantId != null
        ? memberships.find(
            (item) =>
              item.tenant_id === preferredTenantId && isMembershipEnterable(item)
          )
        : null;

    if (preferred) {
      return preferred;
    }

    if (!lastTenantId) {
      return null;
    }

    return (
      memberships.find(
        (item) => item.tenant_id === lastTenantId && isMembershipEnterable(item)
      ) || null
    );
  }, [lastTenantId, memberships, preferredTenantId]);

  const pendingMemberships = useMemo(
    () => memberships.filter((item) => item.status === "pending"),
    [memberships]
  );

  const pendingCount = pendingMemberships.length;

  const activeMemberships = useMemo(
    () => memberships.filter((item) => isMembershipEnterable(item)),
    [memberships]
  );

  const nonRevoked = useMemo(
    () => memberships.filter((item) => item.status !== "revoked"),
    [memberships]
  );

  const invitesOnly = nonRevoked.length > 0 && activeMemberships.length === 0 && pendingCount > 0;

  useEffect(() => {
    document.title = t("auth.select_studio.pageTitle");

    if (!getIdentitySession()) {
      navigate("/login", { replace: true });
    }
  }, [navigate, t]);

  useEffect(() => {
    if (continueMembership && !showAll && continueButtonRef.current) {
      continueButtonRef.current.focus();
    }
  }, [continueMembership, showAll]);

  useEffect(() => {
    if (
      preferredTenantId != null &&
      continueMembership?.tenant_id === preferredTenantId
    ) {
      setRememberPreferred(true);
    }
  }, [continueMembership, preferredTenantId]);

  const syncIdentityMemberships = (next: StudioMembership[]) => {
    const current = getIdentitySession();
    setMemberships(next);

    if (!current) {
      return;
    }

    setIdentitySession({
      ...current,
      memberships: next,
    });
  };

  const applyPreferredPreference = (tenantId: number) => {
    if (rememberPreferred) {
      setPreferredTenantId(tenantId);
    } else if (getPreferredTenantId() === tenantId) {
      clearPreferredTenantId();
    }
  };

  const enterTenantSession = (tenantName?: string | null) => {
    const authSession = getAuthSession();

    if (!authSession) {
      return false;
    }

    if (tenantName) {
      setStudioSwitchBanner(tenantName);
    }

    const returnQuery = new URLSearchParams(location.search).get("return");
    const nextPath = resolvePostAuthRedirect(
      location.state as { from?: { pathname?: string; search?: string } } | null,
      returnQuery
    );

    dispatch(loginSuccess(authSession));
    navigate(nextPath, { replace: true });
    return true;
  };

  const handleSelect = async (membership: StudioMembership) => {
    if (!isMembershipEnterable(membership)) {
      return;
    }

    setBusyTenantId(membership.tenant_id);
    setErrorMessage("");

    try {
      const response = await selectTenant(membership.tenant_id);

      if (response.success && enterTenantSession(membership.tenant_name)) {
        applyPreferredPreference(membership.tenant_id);

        if (response.message) {
          toast.success(response.message);
        }
        return;
      }

      const message =
        response.message ||
        response.error?.message ||
        t("auth.select_studio.genericError");
      setErrorMessage(message);
      toast.error(message);
    } catch (error: unknown) {
      const message =
        (error as { data?: string })?.data || t("auth.select_studio.genericError");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusyTenantId(null);
    }
  };

  const handleAccept = async (membership: StudioMembership) => {
    if (!membership.id) {
      return;
    }

    setBusyMembershipId(membership.id);
    setErrorMessage("");

    try {
      const response = await acceptMembership(membership.id);

      if (!response.success || !response.data) {
        const message =
          response.message ||
          response.error?.message ||
          t("auth.select_studio.inviteError");
        setErrorMessage(message);
        toast.error(message);
        return;
      }

      if (response.data.access_token && enterTenantSession(membership.tenant_name)) {
        if (response.message) {
          toast.success(response.message);
        }
        return;
      }

      syncIdentityMemberships(response.data.memberships || []);
      setShowAll(true);
      toast.success(response.message || t("auth.select_studio.inviteAccepted"));
    } catch {
      const message = t("auth.select_studio.inviteError");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusyMembershipId(null);
    }
  };

  const handleCreateOwnStudio = () => {
    const current = getIdentitySession();

    if (!current?.identityToken || !current.email) {
      setErrorMessage(t("auth.select_studio.create_studio_missing_session"));
      toast.error(t("auth.select_studio.create_studio_missing_session"));
      return;
    }

    storeGoogleSignupDraft({
      identity_token: current.identityToken,
      email: current.email,
      name: current.name || "",
    });
    navigate("/register?continue=1");
  };

  const handleDecline = async (membership: StudioMembership) => {
    if (!membership.id) {
      return;
    }

    const studioName = membership.tenant_name || t("auth.select_studio.unnamed");
    const confirmation = await Swal.fire({
      title: t("auth.select_studio.decline_confirm_title"),
      text: t("auth.select_studio.decline_confirm", { name: studioName }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("auth.select_studio.decline_invite"),
      cancelButtonText: t("auth.select_studio.decline_cancel"),
      confirmButtonColor: "#f06548",
      cancelButtonColor: "#878a99",
      focusCancel: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setBusyMembershipId(membership.id);
    setErrorMessage("");

    try {
      const response = await declineMembership(membership.id);

      if (!response.success || !response.data) {
        const message =
          response.message ||
          response.error?.message ||
          t("auth.select_studio.inviteError");
        setErrorMessage(message);
        toast.error(message);
        return;
      }

      const next = response.data.memberships || [];
      syncIdentityMemberships(next);
      toast.success(response.message || t("auth.select_studio.inviteDeclined"));

      if (next.filter((item) => item.status !== "revoked").length === 0) {
        clearIdentitySession();
        navigate("/login", { replace: true });
      }
    } catch {
      const message = t("auth.select_studio.inviteError");
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setBusyMembershipId(null);
    }
  };

  if (!session) {
    return null;
  }

  const visibleMemberships =
    showAll || !continueMembership || invitesOnly
      ? nonRevoked
      : memberships.filter(
          (item) =>
            item.tenant_id === continueMembership.tenant_id ||
            item.status === "pending"
        );

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <Link to="/" className="d-inline-block auth-logo">
                    <AdatisLogo variant="light" linkTo={false} />
                  </Link>
                  <p className="mt-3 fs-15 fw-medium">{t("auth.select_studio.tagline")}</p>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={10} lg={8} xl={7}>
                <div className="bg-white rounded p-4 shadow-sm">
                  <div className="text-center mb-4">
                    <h5 className="text-primary">{t("auth.select_studio.title")}</h5>
                    <p className="text-muted mb-0">{t("auth.select_studio.subtitle")}</p>
                  </div>

                  {errorMessage ? <Alert color="danger">{errorMessage}</Alert> : null}

                  {invitesOnly ? (
                    <Alert color="info">
                      <strong className="d-block mb-1">
                        {t("auth.select_studio.invites_only_title")}
                      </strong>
                      {t("auth.select_studio.invites_only_body")}
                    </Alert>
                  ) : null}

                  {!invitesOnly && pendingCount > 0 ? (
                    <Alert color="info">
                      {t("auth.select_studio.pending_hint", { count: pendingCount })}
                    </Alert>
                  ) : null}

                  {nonRevoked.length === 0 ? (
                    <div className="text-center py-4 px-2">
                      <div className="avatar-lg mx-auto mb-3">
                        <div className="avatar-title bg-soft-warning text-warning rounded-circle fs-24">
                          <i className="ri-store-2-line" />
                        </div>
                      </div>
                      <h5 className="mb-2">{t("auth.select_studio.empty_title")}</h5>
                      <p className="text-muted mb-4">{t("auth.select_studio.empty_body")}</p>
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        {session.identityToken && session.email ? (
                          <Button color="primary" onClick={handleCreateOwnStudio}>
                            {t("auth.select_studio.empty_cta_register")}
                          </Button>
                        ) : (
                          <Button color="primary" tag={Link} to="/register">
                            {t("auth.select_studio.empty_cta_register")}
                          </Button>
                        )}
                        <Button color="light" tag={Link} to="/login">
                          {t("auth.select_studio.back_to_login")}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {continueMembership && !showAll && !invitesOnly ? (
                        <div className="mb-4 p-3 border border-primary rounded bg-light">
                          <p className="text-muted mb-2 fs-13">
                            {preferredTenantId === continueMembership.tenant_id
                              ? t("auth.select_studio.preferred_hint")
                              : t("auth.select_studio.continue_hint")}
                          </p>
                          <Button
                            innerRef={continueButtonRef}
                            color="primary"
                            size="lg"
                            className="w-100 mb-2"
                            disabled={busyTenantId === continueMembership.tenant_id}
                            aria-label={t("auth.select_studio.continue", {
                              name:
                                continueMembership.tenant_name ||
                                t("auth.select_studio.unnamed"),
                            })}
                            onClick={() => handleSelect(continueMembership)}
                          >
                            {busyTenantId === continueMembership.tenant_id ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                                {t("auth.select_studio.loading")}
                              </>
                            ) : (
                              t("auth.select_studio.continue", {
                                name:
                                  continueMembership.tenant_name ||
                                  t("auth.select_studio.unnamed"),
                              })
                            )}
                          </Button>
                          <FormGroup check className="mb-2">
                            <Input
                              id="remember-preferred-studio"
                              type="checkbox"
                              checked={rememberPreferred}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setRememberPreferred(checked);

                                if (!checked) {
                                  clearPreferredTenantId();
                                }
                              }}
                            />
                            <Label check htmlFor="remember-preferred-studio" className="fs-13">
                              {t("auth.select_studio.always_enter")}
                            </Label>
                          </FormGroup>
                          <Button
                            color="link"
                            className="w-100"
                            onClick={() => setShowAll(true)}
                          >
                            {t("auth.select_studio.view_all")}
                          </Button>
                        </div>
                      ) : null}

                      {(showAll || !continueMembership || invitesOnly || pendingCount > 0) && (
                        <Row className="g-3">
                          {visibleMemberships.map((membership) => (
                            <Col
                              md={6}
                              key={`${membership.id ?? membership.tenant_id}-${membership.status}`}
                            >
                              <StudioMembershipCard
                                membership={membership}
                                highlighted={
                                  continueMembership?.tenant_id === membership.tenant_id
                                }
                                busy={
                                  busyTenantId === membership.tenant_id ||
                                  busyMembershipId === membership.id
                                }
                                onSelect={handleSelect}
                                onAccept={handleAccept}
                                onDecline={handleDecline}
                              />
                            </Col>
                          ))}
                        </Row>
                      )}

                      {(showAll || invitesOnly) && activeMemberships.length > 0 ? (
                        <FormGroup check className="mt-3">
                          <Input
                            id="remember-preferred-studio-all"
                            type="checkbox"
                            checked={rememberPreferred}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setRememberPreferred(checked);

                              if (!checked) {
                                clearPreferredTenantId();
                              }
                            }}
                          />
                          <Label check htmlFor="remember-preferred-studio-all" className="fs-13">
                            {t("auth.select_studio.always_enter")}
                          </Label>
                        </FormGroup>
                      ) : null}
                    </>
                  )}

                  {nonRevoked.length > 0 ? (
                    <div className="text-center mt-4 pt-3 border-top">
                      <p className="text-muted fs-13 mb-2">
                        {t("auth.select_studio.create_studio_hint")}
                      </p>
                      <Button
                        color="outline-primary"
                        className="w-100 w-md-auto"
                        onClick={handleCreateOwnStudio}
                        disabled={!session.identityToken || !session.email}
                      >
                        {t("auth.select_studio.create_studio_cta")}
                      </Button>
                    </div>
                  ) : null}

                  <div className="text-center mt-4 d-flex flex-column align-items-center gap-2">
                    <HelpArticleLink
                      articlePath={HELP_ARTICLE_ACCESS_LOGIN}
                      label={t("auth.select_studio.help_link")}
                    />
                    <Link to="/login" className="fw-medium text-primary">
                      {t("auth.select_studio.back_to_login")}
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default SelectStudio;
